import { CanvasLogger } from '../../../../utils/loggers/CanvasLogger';

// Module-level singleton LRU cache for Path2D
const PATH2D_MAX_GLOBAL = 16000;
const path2dCache = new Map<string, Path2D>(); // insertion order = LRU

// Sentinel value for glyphs with no outline (spaces, controls, etc.)
// Use Symbol.for() to create a global symbol that's the same across all modules
const EMPTY_GLYPH_SENTINEL = Symbol.for('empty-glyph');
function lruSet(key: string, value: Path2D) {
  if (path2dCache.has(key)) path2dCache.delete(key);
  path2dCache.set(key, value);
  if (path2dCache.size > PATH2D_MAX_GLOBAL) {
    const firstKey = path2dCache.keys().next().value;
    if (firstKey) path2dCache.delete(firstKey);
  }
}
function lruGet(key: string): Path2D | undefined {
  const v = path2dCache.get(key);
  if (!v) return undefined;
  // refresh LRU
  path2dCache.delete(key); path2dCache.set(key, v);
  return v;
}

function quantizeSizePx(sizePx: number): number {
  return Math.round(sizePx * 64) / 64;
}

// Removed unused pxPerUnitFromRun function

function buildPath2DFromOps(ops: any): Path2D | undefined {
  // Support both array-of-ops and compact binary formats.
  if (!ops) {
    console.warn('buildPath2DFromOps: ops is null/undefined');
    return undefined;
  }

  // If we already got a Path2D cached elsewhere, just return it.
  if (ops instanceof Path2D) return ops;

  // Array-of-ops format (current code path)
  if (Array.isArray(ops)) {
    if (ops.length === 0) {
      console.warn('buildPath2DFromOps: ops array is empty');
      return undefined;
    }

    const p = new Path2D();
    let validOps = 0;
    let invalidOps = 0;

    for (const op of ops) {
      const type = op?.type || (op.M && 'M') || (op.L && 'L') || (op.Q && 'Q') || (op.C && 'C') || (op.Z && 'Z');

      try {
        switch (type) {
          case 'M': {
            const o = op.M || op;
            if (typeof o.x === 'number' && typeof o.y === 'number' && isFinite(o.x) && isFinite(o.y)) {
              p.moveTo(o.x, o.y);
              validOps++;
            } else {
              console.warn('buildPath2DFromOps: Invalid M operation:', o);
              invalidOps++;
            }
            break;
          }
          case 'L': {
            const o = op.L || op;
            if (typeof o.x === 'number' && typeof o.y === 'number' && isFinite(o.x) && isFinite(o.y)) {
              p.lineTo(o.x, o.y);
              validOps++;
            } else {
              console.warn('buildPath2DFromOps: Invalid L operation:', o);
              invalidOps++;
            }
            break;
          }
          case 'Q': {
            const o = op.Q || op;
            if (typeof o.x === 'number' && typeof o.y === 'number' && typeof o.x1 === 'number' && typeof o.y1 === 'number' &&
                isFinite(o.x) && isFinite(o.y) && isFinite(o.x1) && isFinite(o.y1)) {
              p.quadraticCurveTo(o.x1, o.y1, o.x, o.y);
              validOps++;
            } else {
              console.warn('buildPath2DFromOps: Invalid Q operation:', o);
              invalidOps++;
            }
            break;
          }
          case 'C': {
            const o = op.C || op;
            if (typeof o.x === 'number' && typeof o.y === 'number' && typeof o.x1 === 'number' && typeof o.y1 === 'number' &&
                typeof o.x2 === 'number' && typeof o.y2 === 'number' &&
                isFinite(o.x) && isFinite(o.y) && isFinite(o.x1) && isFinite(o.y1) && isFinite(o.x2) && isFinite(o.y2)) {
              p.bezierCurveTo(o.x1, o.y1, o.x2, o.y2, o.x, o.y);
              validOps++;
            } else {
              console.warn('buildPath2DFromOps: Invalid C operation:', o);
              invalidOps++;
            }
            break;
          }
          case 'Z': {
            p.closePath();
            validOps++;
            break;
          }
          default: {
            console.warn('buildPath2DFromOps: Unknown operation type:', type, op);
            invalidOps++;
            break;
          }
        }
      } catch (error) {
        console.error('buildPath2DFromOps: Error processing operation:', type, op, error);
        invalidOps++;
      }
    }

    CanvasLogger.glyph.path2d(ops.length, validOps, invalidOps);

    if (validOps === 0) {
      console.error('buildPath2DFromOps: No valid operations found');
      return undefined;
    }

    return p;
  }

  // Binary compact format (ArrayBuffer / TypedArray)
  if (ops.buffer instanceof ArrayBuffer || ops instanceof ArrayBuffer) {
    const buf: ArrayBuffer = ops instanceof ArrayBuffer ? ops : ops.buffer;
    // TODO: replace this stub with your real binary decoder.
    // e.g. const p = decodeGlyphPathBinToPath2D(buf);
    return undefined; // <- until decoder is wired, fall back to rectangles below
  }

  console.warn('buildPath2DFromOps: Unsupported ops format:', typeof ops);
  return undefined;
}

export class EngineTextRenderer {
  private ctx: CanvasRenderingContext2D;

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  /**
   * Draw a shaped line using glyph paths from cache.
   * Expects a WASM line object with { baseline_y_fp, ascent_fp, descent_fp, x_offset_fp, runs[] }.
   * @param coordinateScale Multiplier to convert world px → device px (includes zoom + DPI)
   */
  drawLineFromSnapshot(line: any, coordinateScale = 1) {
    // console.log('🎨 [TextRenderer] Drawing line:', {
    //   baselineY: (line.baseline_y_fp || 0) / 64.0,
    //   ascentPx: (line.ascent_fp || 0) / 64.0,
    //   descentPx: (line.descent_fp || 0) / 64.0,
    //   xOffset: (line.x_offset_fp || 0) / 64.0,
    //   runCount: line.runs?.length || 0,
    //   coordinateScale
    // });

    // Helper: Convert WASM fixed-point (26.6) → world px → device px
    const toPx = (fp: number) => ((fp || 0) / 64.0) * coordinateScale;

    const baselineY = toPx(line.baseline_y_fp);
    const ascentPx  = toPx(line.ascent_fp);
    const descentPx = toPx(line.descent_fp);
    let currentX    = toPx(line.x_offset_fp);

    // console.log(`🎨 [TextRenderer] Line coordinates:`, {
    //   rawBaselineY: line.baseline_y_fp,
    //   coordinateScale,
    //   scaledBaselineY: baselineY,
    //   ascentPx,
    //   descentPx
    // });

    this.ctx.save();
    this.ctx.fillStyle = '#000';
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 1;

    for (const run of line.runs || []) {
      const fontUid = run.font_fingerprint?.font_uid
                   || run.font_fingerprint?.family
                   || run.font_fingerprint?.postscript_name
                   || 'unknown';
      // Font size in world px (keep for cache keys - independent of zoom/DPI)
      const sizePxWorld = (run.font_size_fp || 0) / 64.0;
      // Use quantized size key for cache stability (must match EdgeDocsCanvas.V3.tsx)
      const quantizeSize = (size: number) => Math.round(size * 64) / 64;
      const sizeKey = quantizeSize(sizePxWorld);

      // WASM glyph paths are already in pixel coordinates - no scaling needed
      const pxPerUnitWorld = 1.0; // WASM outputs pixel coordinates, not design units

      // 🔬 DEBUG (2026-01-25): Log font_size_fp during rendering to diagnose font size issues
      console.log(`🎨 [TextRenderer] Run: font_size_fp=${run.font_size_fp}, sizePxWorld=${sizePxWorld}, sizeKey=${sizeKey}, fontUid=${fontUid}, text="${run.text?.substring(0, 10) || ''}..."`);

      for (const g of run.glyphs || []) {
        const adv   = toPx(g.advance_fp);
        const xOff  = toPx(g.x_offset_fp);
        const gid   = (g.id ?? g.glyph_id ?? g.gid ?? 0) as number;

        // 🔑 Cache key uses WORLD px size (zoom/DPI independent)
        const key   = `${fontUid}@${sizeKey}:${gid}`;

        // console.log(`🎨 [TextRenderer] Processing glyph: gid=${gid}, key=${key}, adv=${adv}, xOff=${xOff}`);

        let p2d = lruGet(key);
        // console.log(`🎨 [TextRenderer] LRU cache lookup for ${key}:`, p2d ? 'HIT' : 'MISS');

        if (!p2d) {
          const ops = (window as any).__edg_glyphOps?.get?.(key);
          // console.log(`🎨 [TextRenderer] Global cache lookup for ${key}:`, ops === EMPTY_GLYPH_SENTINEL ? 'EMPTY_GLYPH' : (ops ? 'FOUND' : 'NOT FOUND'));

          // Handle empty glyphs (spaces, controls, etc.) - just advance, no drawing
          if (ops === EMPTY_GLYPH_SENTINEL) {
            // console.log(`🎨 [TextRenderer] Glyph ${gid} is empty (space/control), advancing only`);
            // Just advance - no drawing needed
          } else if (ops && Array.isArray(ops)) {
            // Build Path2D from operations
            p2d = buildPath2DFromOps(ops);
            // console.log(`🎨 [TextRenderer] Path2D creation result for ${fontUid}@${sizePxWorld}:${gid}:`, p2d ? 'SUCCESS' : 'FAILED');
            if (p2d) {
              lruSet(key, p2d);
            }
          } else if (ops === undefined) {
            // True cache miss - glyph not warmed yet
            // console.log(`🎨 [TextRenderer] ⚠️ True cache miss for ${key} - glyph not warmed yet`);
          }
        }

        const glyphX = currentX + xOff;
        const glyphY = baselineY;

        // Check what we have for this glyph
        const ops = (window as any).__edg_glyphOps?.get?.(key);

        if (ops === EMPTY_GLYPH_SENTINEL) {
          // Empty glyph (space, control character, etc.) - just advance, no drawing
          // console.log(`🎨 [TextRenderer] ⚪ Empty glyph ${gid} - advancing only`);
        } else if (p2d) {
          // We have a Path2D - render the glyph
          this.ctx.save();
          this.ctx.translate(glyphX, glyphY);  // Already in device px (scaled once)
          this.ctx.scale(pxPerUnitWorld, pxPerUnitWorld); // No Y-flip needed - WASM already outputs Y-down
          this.ctx.fillStyle = '#000000';
          this.ctx.fill(p2d);
          this.ctx.restore();

          // console.log(`🎨 [TextRenderer] ✅ Rendered glyph ${gid} at (${glyphX.toFixed(2)}, ${glyphY.toFixed(2)}) with baseline scale ${pxPerUnitWorld.toFixed(6)}`);
        } else if (ops === undefined) {
          // True cache miss - glyph not warmed yet, subtle fallback
          // console.log(`🎨 [TextRenderer] ⏳ Cache miss for glyph ${gid} - will render when available`);
          // For now, just advance - glyph will appear on next incremental render
        } else {
          // Failed to build Path2D from operations
          // console.log(`🎨 [TextRenderer] ❌ FALLBACK RED RECT for glyph ${gid} at (${glyphX.toFixed(2)}, ${glyphY.toFixed(2)}) - key: ${key}`);
          this.ctx.save();
          this.ctx.fillStyle = '#ff0000';
          this.ctx.strokeStyle = '#ff0000';
          this.ctx.lineWidth = 2;
          const h = Math.max(ascentPx + descentPx, 20); // Already scaled
          const w = Math.max(adv, 10); // Already scaled
          this.ctx.fillRect(glyphX, glyphY - ascentPx, w, h);
          this.ctx.strokeRect(glyphX, glyphY - ascentPx, w, h);
          this.ctx.restore();
        }

        currentX += adv;
      }
    }
  }
}
